import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Search, UserCircle, Loader2, Edit, Key, Shield, Activity,
  CheckCircle, XCircle, Smartphone, Monitor, MapPin, Clock, AlertTriangle,
  ShieldCheck, History, Plus
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AccountManagementPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  
  // User Accounts state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Create User state
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    full_name: ''
  });

  // Admin Accounts state
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchAdminQuery, setSearchAdminQuery] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [editAdminMode, setEditAdminMode] = useState(false);
  const [editAdminData, setEditAdminData] = useState({});
  const [createAdminDialog, setCreateAdminDialog] = useState(false);
  const [resetAdminPasswordDialog, setResetAdminPasswordDialog] = useState(false);
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmAdminPassword, setConfirmAdminPassword] = useState('');
  const [changeLogDialog, setChangeLogDialog] = useState(false);
  const [changeLogs, setChangeLogs] = useState([]);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    email: '',
    role: 'ADMIN'
  });

  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery.trim()) {
      const filtered = allUsers.filter(user => 
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(allUsers);
    }
  }, [searchQuery, allUsers]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/account-management/users`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAllUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users list');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSelectUser = async (username) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/account-management/users/${username}/profile`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setUserProfile(response.data);
      setSelectedUser(username);
      setEditData({
        email: response.data.email || '',
        full_name: response.data.full_name || '',
        phone: response.data.phone || '',
        department: response.data.department || ''
      });
      toast.success('User profile loaded');
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      toast.error(error.response?.data?.detail || 'User not found');
      setUserProfile(null);
      setSelectedUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (filteredUsers.length === 1) {
      handleSelectUser(filteredUsers[0].username);
    } else if (filteredUsers.length === 0) {
      toast.error('No users found');
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${BACKEND_URL}/api/admin/account-management/users/${selectedUser}/profile`,
        editData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Profile updated successfully');
      setEditMode(false);
      fetchAllUsers(); // Reload users list
      handleSelectUser(selectedUser); // Reload profile
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${BACKEND_URL}/api/admin/account-management/users/${selectedUser}/reset-password`,
        { new_password: newPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password reset successfully');
      setResetPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = !userProfile.is_active;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${BACKEND_URL}/api/admin/account-management/users/${selectedUser}/status`,
        {
          is_active: newStatus,
          reason: newStatus ? '' : 'Administrative action'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`User ${newStatus ? 'activated' : 'suspended'} successfully`);
      fetchAllUsers(); // Reload users list
      handleSelectUser(selectedUser); // Reload profile
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Create User Function
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        newUser,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('User created successfully');
      setCreateUserDialog(false);
      setNewUser({ username: '', password: '', email: '', full_name: '' });
      fetchAllUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Admin Accounts Functions
  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Failed to load admin accounts');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password || !newAdmin.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${BACKEND_URL}/api/admin/admins`,
        newAdmin,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Admin account created successfully');
      setCreateAdminDialog(false);
      setNewAdmin({ username: '', password: '', email: '', role: 'ADMIN' });
      fetchAdmins();
    } catch (error) {
      console.error('Failed to create admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleSelectAdmin = async (username) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Get admin basic info
      const adminResponse = await axios.get(
        `${BACKEND_URL}/api/admin/admins`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const admin = adminResponse.data.find(a => a.username === username);
      
      if (!admin) {
        toast.error('Admin not found');
        return;
      }

      // Get change logs for admin
      const changeLogResponse = await axios.get(
        `${BACKEND_URL}/api/admin/admins/${username}/changelog`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Get activity logs
      const activityResponse = await axios.get(
        `${BACKEND_URL}/api/admin/logs?admin_username=${username}&limit=50`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setAdminProfile({
        ...admin,
        change_logs: changeLogResponse.data,
        activity_logs: activityResponse.data,
        total_activities: activityResponse.data.length,
        successful_logins: activityResponse.data.filter(log => log.action === 'login' && log.status === 'success').length,
        failed_logins: activityResponse.data.filter(log => log.action === 'login_attempt' && log.status === 'failure').length
      });
      
      setSelectedAdminUser(username);
      setEditAdminData({
        email: admin.email || '',
        role: admin.role || 'ADMIN'
      });
      toast.success('Admin profile loaded');
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to load admin profile');
      setAdminProfile(null);
      setSelectedAdminUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAdminProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${BACKEND_URL}/api/admin/admins/${selectedAdminUser}`,
        editAdminData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Admin profile updated successfully');
      setEditAdminMode(false);
      fetchAdmins();
      handleSelectAdmin(selectedAdminUser);
    } catch (error) {
      console.error('Failed to update admin profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAdminPassword = async () => {
    if (newAdminPassword !== confirmAdminPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newAdminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${BACKEND_URL}/api/admin/admins/${selectedAdminUser}`,
        { password: newAdminPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password reset successfully');
      setResetAdminPasswordDialog(false);
      setNewAdminPassword('');
      setConfirmAdminPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    }
  }, [activeTab]);

  useEffect(() => {
    // Filter admins based on search query
    if (searchAdminQuery.trim()) {
      const filtered = admins.filter(admin => 
        admin.username.toLowerCase().includes(searchAdminQuery.toLowerCase()) ||
        admin.email?.toLowerCase().includes(searchAdminQuery.toLowerCase())
      );
      setFilteredAdmins(filtered);
    } else {
      setFilteredAdmins(admins);
    }
  }, [searchAdminQuery, admins]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-slate-700 dark:text-slate-300" />
            <h1 className="text-3xl font-outfit font-bold text-slate-900 dark:text-white">
              Account Management
            </h1>
          </div>
          <p className="text-slate-600 dark:text-slate-400 font-ibm">
            Manage user and admin accounts, profiles, and access
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-flex">
          <TabsTrigger value="users" className="font-ibm">
            <UserCircle className="h-4 w-4 mr-2" />
            User Accounts
          </TabsTrigger>
          <TabsTrigger value="admins" className="font-ibm">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Admin Accounts
          </TabsTrigger>
        </TabsList>

        {/* User Accounts Tab */}
        <TabsContent value="users" className="space-y-6">

      {/* Users List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-outfit">Users</CardTitle>
              <CardDescription className="font-ibm">
                Select a user to manage their account
              </CardDescription>
            </div>
            <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
              <DialogTrigger asChild>
                <Button className="font-ibm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-outfit">Create New User</DialogTitle>
                  <DialogDescription className="font-ibm">
                    Add a new user account
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="user-username" className="font-ibm">Username *</Label>
                    <Input
                      id="user-username"
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                      placeholder="Enter username"
                      className="font-ibm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-password" className="font-ibm">Password *</Label>
                    <Input
                      id="user-password"
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Enter password"
                      className="font-ibm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-email" className="font-ibm">Email *</Label>
                    <Input
                      id="user-email"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="user@example.com"
                      className="font-ibm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-fullname" className="font-ibm">Full Name</Label>
                    <Input
                      id="user-fullname"
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                      placeholder="John Doe"
                      className="font-ibm"
                    />
                  </div>
                  <Button onClick={handleCreateUser} className="w-full font-ibm">
                    Create User Account
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Search by username, email, or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="font-ibm"
                />
              </div>
            </div>

            {/* Users Table */}
            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
              </div>
            ) : filteredUsers.length > 0 ? (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-ibm font-semibold">Username</TableHead>
                      <TableHead className="font-ibm font-semibold">Email</TableHead>
                      <TableHead className="font-ibm font-semibold">Full Name</TableHead>
                      <TableHead className="font-ibm font-semibold">Status</TableHead>
                      <TableHead className="font-ibm font-semibold">Tokens</TableHead>
                      <TableHead className="font-ibm font-semibold">Last Login</TableHead>
                      <TableHead className="font-ibm font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow
                        key={user.username}
                        className={selectedUser === user.username ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                      >
                        <TableCell className="font-ibm font-medium">{user.username}</TableCell>
                        <TableCell className="font-ibm text-slate-600 dark:text-slate-400">
                          {user.email || 'Not set'}
                        </TableCell>
                        <TableCell className="font-ibm text-slate-600 dark:text-slate-400">
                          {user.full_name || 'Not set'}
                        </TableCell>
                        <TableCell>
                          {user.is_active ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border-red-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Suspended
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-ibm text-slate-600 dark:text-slate-400">
                          {user.active_tokens} / {user.total_tokens}
                        </TableCell>
                        <TableCell className="font-ibm text-slate-600 dark:text-slate-400 text-sm">
                          {user.last_login ? formatDate(user.last_login) : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleSelectUser(user.username)}
                            disabled={loading}
                            className="font-ibm"
                          >
                            View Profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 border border-slate-200 dark:border-slate-700 rounded-lg">
                <p className="text-slate-500 dark:text-slate-400 font-ibm">No users found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Profile Section */}
      {userProfile && (
        <div className="space-y-6">
          {/* Profile Overview */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-outfit flex items-center gap-3">
                    <UserCircle className="h-6 w-6" />
                    {userProfile.username}
                    {userProfile.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 border-red-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Suspended
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="font-ibm mt-2">
                    Member since {formatDate(userProfile.created_at)}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditMode(!editMode)}
                    className="font-ibm"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {editMode ? 'Cancel' : 'Edit Profile'}
                  </Button>
                  <Dialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="font-ibm">
                        <Key className="h-4 w-4 mr-2" />
                        Reset Password
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="font-outfit">Reset Password</DialogTitle>
                        <DialogDescription className="font-ibm">
                          Set a new password for {selectedUser}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="new-password" className="font-ibm">
                            New Password
                          </Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            className="font-ibm"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirm-password" className="font-ibm">
                            Confirm Password
                          </Label>
                          <Input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            className="font-ibm"
                          />
                        </div>
                        <Button
                          onClick={handleResetPassword}
                          disabled={loading}
                          className="w-full font-ibm"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Resetting...
                            </>
                          ) : (
                            'Reset Password'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant={userProfile.is_active ? 'destructive' : 'default'}
                    onClick={handleToggleStatus}
                    disabled={loading}
                    className="font-ibm"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {userProfile.is_active ? 'Suspend' : 'Activate'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-ibm text-slate-600 dark:text-slate-400">
                    Email
                  </Label>
                  {editMode ? (
                    <Input
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="font-ibm"
                    />
                  ) : (
                    <p className="font-ibm font-medium">{userProfile.email || 'Not set'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-slate-600 dark:text-slate-400">
                    Full Name
                  </Label>
                  {editMode ? (
                    <Input
                      value={editData.full_name}
                      onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                      className="font-ibm"
                    />
                  ) : (
                    <p className="font-ibm font-medium">{userProfile.full_name || 'Not set'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-slate-600 dark:text-slate-400">
                    Phone
                  </Label>
                  {editMode ? (
                    <Input
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="font-ibm"
                    />
                  ) : (
                    <p className="font-ibm font-medium">{userProfile.phone || 'Not set'}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-slate-600 dark:text-slate-400">
                    Department
                  </Label>
                  {editMode ? (
                    <Input
                      value={editData.department}
                      onChange={(e) => setEditData({ ...editData, department: e.target.value })}
                      className="font-ibm"
                    />
                  ) : (
                    <p className="font-ibm font-medium">{userProfile.department || 'Not set'}</p>
                  )}
                </div>
              </div>

              {editMode && (
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEditMode(false)} className="font-ibm">
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateProfile} disabled={loading} className="font-ibm">
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Smartphone className="h-6 w-6 text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Total Tokens</p>
                    <p className="text-2xl font-outfit font-bold">{userProfile.total_tokens}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Active Tokens</p>
                    <p className="text-2xl font-outfit font-bold">{userProfile.active_tokens}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Activity className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                  </div>
                  <div>
                    <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Total Logins</p>
                    <p className="text-2xl font-outfit font-bold">{userProfile.total_logins}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-300" />
                  </div>
                  <div>
                    <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Failed Logins</p>
                    <p className="text-2xl font-outfit font-bold">{userProfile.failed_logins?.length || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tokens Table */}
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">User Tokens</CardTitle>
              <CardDescription className="font-ibm">
                Manage tokens assigned to this user
              </CardDescription>
            </CardHeader>
            <CardContent>
              {userProfile.tokens && userProfile.tokens.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-ibm font-semibold">Name</TableHead>
                      <TableHead className="font-ibm font-semibold">Type</TableHead>
                      <TableHead className="font-ibm font-semibold">Status</TableHead>
                      <TableHead className="font-ibm font-semibold">Created</TableHead>
                      <TableHead className="font-ibm font-semibold">Last Login</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userProfile.tokens.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell className="font-ibm">
                          <div className="flex items-center gap-2">
                            {token.token_type === 'soft-token' ? (
                              <Smartphone className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Monitor className="h-4 w-4 text-purple-600" />
                            )}
                            {token.name}
                          </div>
                        </TableCell>
                        <TableCell className="font-ibm">{token.token_type}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              token.status === 'active'
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                : 'bg-slate-100 text-slate-700 border-slate-200'
                            }
                          >
                            {token.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-ibm text-slate-600">
                          {formatDate(token.created_at)}
                        </TableCell>
                        <TableCell className="font-ibm text-slate-600">
                          {formatDate(token.last_login)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 font-ibm">No tokens found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Login History */}
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Login History
              </CardTitle>
              <CardDescription className="font-ibm">
                Recent login activity (last 50 logins)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {userProfile.login_history && userProfile.login_history.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {userProfile.login_history.map((log, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-slate-500" />
                        <div>
                          <p className="font-ibm font-medium text-sm">
                            {log.region || 'Unknown Location'}
                          </p>
                          <p className="font-ibm text-xs text-slate-500">
                            {formatDate(log.timestamp)}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                        Success
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 font-ibm">No login history found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Failed Login Attempts */}
          {userProfile.failed_logins && userProfile.failed_logins.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-5 w-5" />
                  Failed Login Attempts
                </CardTitle>
                <CardDescription className="font-ibm">
                  Recent failed authentication attempts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userProfile.failed_logins.map((log, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
                    >
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <div>
                          <p className="font-ibm font-medium text-sm">
                            {log.region || 'Unknown Location'}
                          </p>
                          <p className="font-ibm text-xs text-slate-500">
                            {formatDate(log.timestamp)}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-red-100 text-red-800 border-red-200">
                        Failed
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
        </TabsContent>

        {/* Admin Accounts Tab */}
        <TabsContent value="admins" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">Admin Accounts</CardTitle>
                  <CardDescription className="font-ibm">
                    Select an admin to manage their account
                  </CardDescription>
                </div>
                <Dialog open={createAdminDialog} onOpenChange={setCreateAdminDialog}>
                  <DialogTrigger asChild>
                    <Button className="font-ibm">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-outfit">Create New Admin</DialogTitle>
                      <DialogDescription className="font-ibm">
                        Add a new administrator account
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="admin-username" className="font-ibm">Username *</Label>
                        <Input
                          id="admin-username"
                          value={newAdmin.username}
                          onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                          placeholder="Enter username"
                          className="font-ibm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-password" className="font-ibm">Password *</Label>
                        <Input
                          id="admin-password"
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                          placeholder="Enter password"
                          className="font-ibm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-email" className="font-ibm">Email *</Label>
                        <Input
                          id="admin-email"
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                          placeholder="admin@example.com"
                          className="font-ibm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-role" className="font-ibm">Role</Label>
                        <select
                          id="admin-role"
                          value={newAdmin.role}
                          onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md font-ibm"
                        >
                          <option value="SUPER_ADMIN">Super Admin</option>
                          <option value="ADMIN">Admin</option>
                          <option value="SUPPORT_ADMIN">Support Admin</option>
                          <option value="HELPDESK_ADMIN">Helpdesk Admin</option>
                        </select>
                      </div>
                      <Button onClick={handleCreateAdmin} className="w-full font-ibm">
                        Create Admin Account
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="Search by username or email..."
                      value={searchAdminQuery}
                      onChange={(e) => setSearchAdminQuery(e.target.value)}
                      className="font-ibm"
                    />
                  </div>
                </div>

                {/* Admins Table */}
                {loadingAdmins ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
                  </div>
                ) : filteredAdmins.length > 0 ? (
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-ibm font-semibold">Username</TableHead>
                          <TableHead className="font-ibm font-semibold">Email</TableHead>
                          <TableHead className="font-ibm font-semibold">Role</TableHead>
                          <TableHead className="font-ibm font-semibold">Created</TableHead>
                          <TableHead className="font-ibm font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAdmins.map((admin) => (
                          <TableRow
                            key={admin.username}
                            className={selectedAdminUser === admin.username ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                          >
                            <TableCell className="font-ibm font-medium">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-blue-600" />
                                {admin.username}
                              </div>
                            </TableCell>
                            <TableCell className="font-ibm text-slate-600 dark:text-slate-400">
                              {admin.email || 'Not set'}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                admin.role === 'SUPER_ADMIN'
                                  ? 'bg-purple-100 text-purple-800 border-purple-200'
                                  : admin.role === 'ADMIN'
                                  ? 'bg-blue-100 text-blue-800 border-blue-200'
                                  : 'bg-slate-100 text-slate-700 border-slate-200'
                              }>
                                {admin.role.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-ibm text-slate-600 dark:text-slate-400 text-sm">
                              {formatDate(admin.created_at)}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => handleSelectAdmin(admin.username)}
                                disabled={loading}
                                className="font-ibm"
                              >
                                View Profile
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <p className="text-slate-500 dark:text-slate-400 font-ibm">No admin accounts found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Admin Profile Section */}
          {adminProfile && (
            <div className="space-y-6">
              {/* Profile Overview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-outfit flex items-center gap-3">
                        <ShieldCheck className="h-6 w-6" />
                        {adminProfile.username}
                        <Badge className={
                          adminProfile.role === 'SUPER_ADMIN'
                            ? 'bg-purple-100 text-purple-800 border-purple-200'
                            : 'bg-blue-100 text-blue-800 border-blue-200'
                        }>
                          {adminProfile.role.replace('_', ' ')}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="font-ibm mt-2">
                        Admin since {formatDate(adminProfile.created_at)}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditAdminMode(!editAdminMode)}
                        className="font-ibm"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {editAdminMode ? 'Cancel' : 'Edit Profile'}
                      </Button>
                      <Dialog open={resetAdminPasswordDialog} onOpenChange={setResetAdminPasswordDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="font-ibm">
                            <Key className="h-4 w-4 mr-2" />
                            Reset Password
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-outfit">Reset Password</DialogTitle>
                            <DialogDescription className="font-ibm">
                              Set a new password for {selectedAdminUser}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="new-admin-password" className="font-ibm">
                                New Password
                              </Label>
                              <Input
                                id="new-admin-password"
                                type="password"
                                value={newAdminPassword}
                                onChange={(e) => setNewAdminPassword(e.target.value)}
                                placeholder="Enter new password"
                                className="font-ibm"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="confirm-admin-password" className="font-ibm">
                                Confirm Password
                              </Label>
                              <Input
                                id="confirm-admin-password"
                                type="password"
                                value={confirmAdminPassword}
                                onChange={(e) => setConfirmAdminPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="font-ibm"
                              />
                            </div>
                            <Button
                              onClick={handleResetAdminPassword}
                              disabled={loading}
                              className="w-full font-ibm"
                            >
                              {loading ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Resetting...
                                </>
                              ) : (
                                'Reset Password'
                              )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="font-ibm text-slate-600 dark:text-slate-400">
                        Email
                      </Label>
                      {editAdminMode ? (
                        <Input
                          value={editAdminData.email}
                          onChange={(e) => setEditAdminData({ ...editAdminData, email: e.target.value })}
                          className="font-ibm"
                        />
                      ) : (
                        <p className="font-ibm font-medium">{adminProfile.email || 'Not set'}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="font-ibm text-slate-600 dark:text-slate-400">
                        Role
                      </Label>
                      {editAdminMode ? (
                        <select
                          value={editAdminData.role}
                          onChange={(e) => setEditAdminData({ ...editAdminData, role: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md font-ibm"
                        >
                          <option value="SUPER_ADMIN">Super Admin</option>
                          <option value="ADMIN">Admin</option>
                          <option value="SUPPORT_ADMIN">Support Admin</option>
                          <option value="HELPDESK_ADMIN">Helpdesk Admin</option>
                        </select>
                      ) : (
                        <p className="font-ibm font-medium">{adminProfile.role.replace('_', ' ')}</p>
                      )}
                    </div>
                  </div>

                  {editAdminMode && (
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setEditAdminMode(false)} className="font-ibm">
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateAdminProfile} disabled={loading} className="font-ibm">
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                        <Activity className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                      </div>
                      <div>
                        <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Total Activities</p>
                        <p className="text-2xl font-outfit font-bold">{adminProfile.total_activities}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-300" />
                      </div>
                      <div>
                        <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Successful Logins</p>
                        <p className="text-2xl font-outfit font-bold">{adminProfile.successful_logins}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-300" />
                      </div>
                      <div>
                        <p className="text-sm font-ibm text-slate-600 dark:text-slate-400">Failed Logins</p>
                        <p className="text-2xl font-outfit font-bold">{adminProfile.failed_logins}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Change Log */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Change Log
                  </CardTitle>
                  <CardDescription className="font-ibm">
                    History of actions performed by or on this admin account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {adminProfile.change_logs && adminProfile.change_logs.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {adminProfile.change_logs.map((log, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex-1">
                            <p className="font-ibm font-medium text-sm">{log.action}</p>
                            <p className="font-ibm text-xs text-slate-500 mt-1">{log.details}</p>
                            <p className="font-ibm text-xs text-slate-400 mt-1">
                              {formatDate(log.timestamp)}
                            </p>
                          </div>
                          <Badge className={
                            log.status === 'success'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : 'bg-red-100 text-red-800 border-red-200'
                          }>
                            {log.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-slate-500 font-ibm">No change log entries found</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Activity History */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Activity History
                  </CardTitle>
                  <CardDescription className="font-ibm">
                    Recent administrative actions (last 50 activities)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {adminProfile.activity_logs && adminProfile.activity_logs.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {adminProfile.activity_logs.map((log, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-center gap-3">
                            <Activity className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="font-ibm font-medium text-sm">
                                {log.action.replace('_', ' ')}
                              </p>
                              <p className="font-ibm text-xs text-slate-500 mt-1">
                                {log.details}
                              </p>
                              <p className="font-ibm text-xs text-slate-400 mt-1">
                                {formatDate(log.timestamp)}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            log.status === 'success'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : 'bg-red-100 text-red-800 border-red-200'
                          }>
                            {log.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-slate-500 font-ibm">No activity history found</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
